import {
  Box,
  Card,
  CardBody,
  Divider,
  Flex,
  Tag,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useRef } from 'react';
import { IconButton } from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import WatchLaterOutlinedIcon from "@mui/icons-material/WatchLaterOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { PlaylistAddCheck } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const getStatus = (status) => {
  switch (status) {
    case 'concluded':
      return 'Completed';
    case 'in_progress':
      return 'Processing';
    default:
      return status;
  }
};

const StatusTag = ({ status }) => {
  switch (status) {
    case 'concluded':
      return (
        <Tag variant="subtle" minW="100px" bg="#E6F7ED" marginY={-1.5}>
          <IconButton sx={{ paddingX:0, paddingY:0.5}}><CheckCircleOutlinedIcon sx={{ padding:0, fontSize: 20, color: "#5d5d5d" }}/></IconButton>
          <TagLabel color="#009652" pl="2px" fontSize="14px">
            {getStatus(status)}
          </TagLabel>
        </Tag>
      );
    case 'in_progress':
      return (
        <Tag variant="subtle" minW="100px" bgColor="#E7F1FC" marginY={-1.5}>
          <IconButton sx={{ paddingX:0, paddingY:0.5}}><WatchLaterOutlinedIcon sx={{ padding:0, fontSize: 20, color: "#1476DE" }}/></IconButton>
          <TagLabel color="#1476DE" pl="2px" fontSize="14px">
            {getStatus(status)}
          </TagLabel>
        </Tag>
      );
    default:
      return (
        <Tag variant="subtle" minW="100px" bg="#E6F7ED" marginY={-1.5}>
          <IconButton sx={{ paddingX:0, paddingY:0.5}}><CheckCircleOutlinedIcon sx={{ padding:0, fontSize: 20, color: "#5d5d5d" }}/></IconButton>
          <TagLabel color="#009652" pl="2px" fontSize="14px">
            {getStatus(status)}
          </TagLabel>
        </Tag>
      );
  }
};

const ActionHeading = ({ step, isStepOpen, setIsStepOpen }) => {
  const { stepNumber, stepHeading, status } = step;
  return (
    <Flex
      alignItems="baseline"
      gap={1}
      px={0}
      borderRadius="4px"
      justifyContent="space-between"
      fontSize="14px"
      onClick = {() => (setIsStepOpen((prevState) => !prevState))} 
    >
      <Flex alignItems="center" gap={"4px"} color="#333333" fontWeight={600} mr={"7%"}>
        <IconButton sx={{ padding: 0, "&:hover":{backgroundColor: "transparent"} }}>{isStepOpen ? 
            <KeyboardArrowDownIcon sx={{ padding:0, fontSize: 20, color: "#8d8d8d" }}/> : 
            <KeyboardArrowRightIcon sx={{ padding:0, fontSize: 20, color: "#8d8d8d" }}/>}
        </IconButton>
        <Flex minW="12px" paddingY={1}>
          <Box>{`${stepNumber}`}</Box>
          <Box paddingRight={"4px"}>.</Box>
          <Box wordBreak="break-word" overflowWrap="break-word">
            <Text fontWeight="regular" color="#333333" fontFamily="sans-serif" fontSize="14px">
              {stepHeading}
            </Text>
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

const ActionSubheading = ({ message }) => {
  return (
    <Flex
      py={1}
      pl={"35px"}
      ml={1}
      mr={"6%"}
      fontSize="13.5px"
      wordBreak="break-word"
      overflowWrap="break-word"
    >
      <Text fontWeight="regular" color="#3D3D3D" fontFamily="sans-serif">
        {message}
      </Text>
    </Flex>
  );
};

const Actions = ({ myActionMessages }) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const actionbodyRef = React.useRef(null);

  useEffect(() => {
    if (actionbodyRef.current) {
      actionbodyRef.current.scrollTop = actionbodyRef.current.scrollHeight;
    }
  }, [myActionMessages]);
  
  if (!myActionMessages || myActionMessages.length === 0) return null;

  return (
    <Card
      variant="outline"
      overflowY="auto"
      data-scrollable="true"
      size="lg"
      maxWidth="100%"
      maxH={'44vh'}
      ref={actionbodyRef}
      backgroundColor={"#f6faff"}
    >
      <Flex
        px={0}
        // py={1}
        height={"40px"}
        onClick={() => setIsOpen((prevState) => !prevState)}
        cursor={'pointer'}
        alignItems={'center'}
        // justifyContent={"center"}
        gap={2}
        justifyContent={'space-between'}
        position="sticky" // Add sticky positioning
        top="0" // Ensure it sticks to the top of the container
        zIndex="1" // Set z-index to ensure it appears above other content
        bg="#f6faff"
        borderBottom="1px solid #E5E5E5"
        // maxH={'20%'}
      >
        <Flex gap={"4px"} position="sticky" alignItems={'space-between'} paddingLeft={'16px'} justifyContent={"flex-end"} height={"40px"}>
          <IconButton sx={{ padding:0, borderRadius: "0%", "&:hover":{backgroundColor: "transparent"} }}>
            <PlaylistAddCheck sx={{padding:0, fontSize: "24px", color: "#5d5d5d" }}/>
          </IconButton>
          <Text paddingLeft="2px" pt={"2px"} fontWeight="regular" color="#1d1d1d" fontSize="14px" fontFamily="sans-serif" alignSelf={"center"}>Actions</Text>
        </Flex>
        <IconButton sx={{ padding:0, fontSize: "24px", color: "#adadad", marginRight:"16px", "&:hover":{backgroundColor: "transparent"} }} >
          { isOpen ? <ExpandMoreIcon/> : <ExpandLessIcon/> }
        </IconButton>
      </Flex>
      
      <AnimatePresence>
      {isOpen && (
          <CardBody px={"16px"} py={3}>
            {myActionMessages.map((step) => (
              <ActionStepMessages step={step}/>
            ))}
          </CardBody>
      )}
      </AnimatePresence>
    </Card>
  );
};

const ActionStepMessages = ({step}) => {
  const [isStepOpen, setIsStepOpen] = React.useState(true);
  return (
    <Box mb={0} key={step.stepNumber}>
      <ActionHeading step={step} isStepOpen={isStepOpen} setIsStepOpen={setIsStepOpen}/>
      {isStepOpen ? step.actionMessages.map((message, index) => (
        <ActionSubheading message={message} key={index} />
      )): null}
    </Box>
  )
}

export default Actions;
